/**
 * IndexesTab — index section for `TableEditor`. Read-only when `editContext`
 * is null (Slice 0.4 scaffolding). When `editContext` is supplied (Slice C1+)
 * the `[+ Index]` button opens an `IndexAddPopover` with name + columns +
 * UNIQUE checkbox + filtered-index `WHERE …` expression. Filter is gated to
 * the engines that support it (mssql / postgres / sqlite); mysql + oracle
 * render it disabled with the documented C3 functional-index workaround
 * tooltip.
 *
 * Per-row affordances:
 *   - Lifecycle (Slice C5): per-engine action menu (REBUILD / REORGANIZE /
 *     REINDEX / SWAP). mysql disables the trigger with the documented
 *     table-level redirect. This is the ONLY live per-row action.
 *
 * Edit + Drop are deliberately NOT rendered: neither was ever wired in either
 * variant (no code path enabled them), so permanently dead icons would be pure
 * noise — “Edit as SQL” covers changing or dropping an index.
 *
 * `compact` also suppresses the section label, because the popover's tab strip
 * directly above already reads “Indexes (n)”.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.4 (read-only) + § Slice C1 (filter)
 *      + § Slice C5 (lifecycle).
 */
import { Plus, RefreshCw } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import type { DdlStructure, IndexDef } from '../../../../sql-resolver/types/ddlEdit';
import { DisabledAffordance } from '../common/DisabledAffordance';
import { IndexAddPopover } from '../common/IndexPopovers';
import { IndexLifecycleMenu } from '../common/IndexLifecycleMenu';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { indexRowsFromStructure } from './tableFacetCounts';

const ADD_INDEX_TOOLTIP =
  'Adding indexes isn’t available in this read-only view — use “Edit as SQL” to add an index.';
const LIFECYCLE_TOOLTIP =
  'Index lifecycle actions aren’t available in this read-only view — use “Edit as SQL” instead.';
const EMPTY_INDEXES_HINT = 'No indexes defined yet — add one with + Index.';

/** A key spelled with delimiters (`[Email]`, `"email"`, `` `email` ``) reads
 *  as its name, the way the Object Explorer lists index columns. Only a plain
 *  delimited name is unwrapped — an escaped delimiter inside stays verbatim. */
function displayKey(spelling: string): string {
  const match = /^(?:\[([^[\]]+)\]|"([^"]+)"|`([^`]+)`)$/.exec(spelling);
  return match ? (match[1] ?? match[2] ?? match[3]) : spelling;
}

function describeIndex(idx: IndexDef): string {
  const cols = idx.columns
    .map((c) => (typeof c === 'string' ? displayKey(c) : c.kind === 'expression' ? c.text : c.name))
    .join(', ');
  return cols.length > 0 ? cols : '—';
}

export interface IndexesTabProps {
  readonly structure: DdlStructure | null;
  /** Required `data-testid` prefix for unit + E2E specs. */
  readonly testId?: string;
  /** Optional edit context — when supplied, the `[+ Index]` affordance
   *  opens an `IndexAddPopover`. When null/undefined, the affordance stays
   *  disabled (Slice 0.4 scaffolding). */
  readonly editContext?: VqeDdlEditContext | null;
  /** Compact density (compact Visual DDL popover): tighter cells + smaller
   *  action buttons. Structure is unchanged. */
  readonly compact?: boolean;
  /** The indexes of the CREATE INDEX statements that follow `structure` in
   *  the same document — the Table Designer's buffer spells a table's
   *  existing indexes that way. Listed after the structure's own row. */
  readonly indexes?: readonly IndexDef[];
}

export function IndexesTab({
  structure,
  testId = 'ddl-indexes-tab',
  editContext,
  compact = false,
  indexes,
}: IndexesTabProps) {
  const rows = indexRowsFromStructure(structure, indexes);
  const headClass = compact ? 'h-8 px-2 py-1 text-xs' : undefined;
  const cellClass = compact ? 'px-2 py-1' : undefined;
  const iconButtonClass = compact
    ? 'h-6 w-6 text-muted-foreground'
    : 'h-7 w-7 text-muted-foreground';

  return (
    <div
      className={cn('flex flex-col', compact ? 'gap-2' : 'gap-3')}
      data-testid={testId}
    >
      <div className={cn('flex items-center', compact ? 'justify-end' : 'justify-between')}>
        {compact ? null : (
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Indexes ({rows.length})
          </div>
        )}
        {editContext ? (
          <IndexAddPopover editContext={editContext} testId={`${testId}-add-index`} />
        ) : (
          <DisabledAffordance
            testId={`${testId}-add-index`}
            tooltip={ADD_INDEX_TOOLTIP}
            variant="ghost"
            className={compact ? 'h-6 gap-1 px-1.5 text-xs text-muted-foreground' : undefined}
          >
            <Plus className="h-3.5 w-3.5" />
            Index
          </DisabledAffordance>
        )}
      </div>

      {rows.length === 0 ? (
        <div
          className={cn(
            'rounded-md border border-dashed border-border text-center text-xs text-muted-foreground',
            compact ? 'px-2 py-3' : 'px-3 py-6',
          )}
          data-testid={`${testId}-empty`}
        >
          {EMPTY_INDEXES_HINT}
        </div>
      ) : (
        <Table data-testid={`${testId}-table`}>
          <TableHeader>
            <TableRow>
              <TableHead className={cn('w-[30%]', headClass)}>Name</TableHead>
              <TableHead className={cn('w-[15%] text-center', headClass)}>Unique</TableHead>
              <TableHead className={cn('w-[40%]', headClass)}>Columns</TableHead>
              <TableHead className={cn('w-[15%] text-right', headClass)}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((idx, i) => (
              <TableRow key={`${idx.name.raw}-${i}`} data-testid={`${testId}-row-${idx.name.name}`}>
                <TableCell className={cn('font-mono text-xs', cellClass)}>
                  {idx.name.raw || idx.name.name}
                </TableCell>
                <TableCell className={cn('whitespace-nowrap text-center text-xs', cellClass)}>
                  {idx.unique ? (
                    'YES'
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className={cn('font-mono text-xs', cellClass)}>
                  {describeIndex(idx)}
                </TableCell>
                <TableCell className={cn('text-right', cellClass)}>
                  <div className="flex items-center justify-end gap-1">
                    {editContext ? (
                      <IndexLifecycleMenu
                        editContext={editContext}
                        index={idx}
                        testId={`${testId}-lifecycle-${idx.name.name}`}
                      />
                    ) : (
                      <DisabledAffordance
                        testId={`${testId}-lifecycle-${idx.name.name}`}
                        tooltip={LIFECYCLE_TOOLTIP}
                        variant="ghost"
                        size="icon"
                        className={iconButtonClass}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </DisabledAffordance>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
